import { verifyOAuthTokenApi } from "@/api/services/admin/auth";
import { getProductContractHandoffApi } from "@/api/services/admin/product-contract";
import { TProductContractHandoffDataDto } from "@/api/services/admin/product-contract/dtos/get-product-contract-handoff.dto";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { AppRoutes } from "@/routes/config";
import { ADMIN_EMAIL_KEY, CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { setToken } from "@/utils/token";
import { getDynamicRoute, isValidRedirectUrl, withCenterFrom } from "@/utils/url";
import Cookies from "js-cookie";
import { FC, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Resolves where a Center handoff lands. The Center only passes the contract state
 * (`contractToken`); building the route is this app's own responsibility.
 *   - referenceProductId set → the 紹介会社 already exists here → its detail page
 *   - referenceProductId null + unconsumed contract → the initial registration page
 * Anything else (no contract, or a hand-off already consumed) falls back to the match list.
 */
const resolveHandoffRoute = (contract: TProductContractHandoffDataDto | null, isSigned: boolean): string => {
  if (!contract) return AppRoutes.duringContractList;

  if (contract.referenceProductId) {
    return getDynamicRoute(AppRoutes.matchDetail, { id: String(contract.referenceProductId) });
  }

  if (!isSigned) return AppRoutes.matchCreate;

  return AppRoutes.duringContractList;
};

/**
 * OAuth hand-off landing page for the Center → Match admin SSO flow.
 *
 * The Center mints `?token=...` plus either `contractToken` (a handed-off product contract, which
 * decides where to land) or `redirectUrl` (a deep link), then sends the browser here.
 */
export const AdminAuthCallbackPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const contractToken = searchParams.get("contractToken");
  const redirectUrl = searchParams.get("redirectUrl");
  // 遷移元のcenterパス。契約の状態ではないので contractToken には含まれず、このURLに直接付いてくる。
  const sourcePath = searchParams.get("sourcePath");

  // The hand-off token is single-use server-side, so it must only be posted once.
  const isProcessing = useRef(false);

  const verify = useCallback(async () => {
    if (isProcessing.current) return;
    isProcessing.current = true;

    // Revoke + clear any stale session before starting a fresh OAuth handoff.
    // Must finish before setToken() below, or it would wipe the new session.
    await logout();

    if (!token) {
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    try {
      const payload = await verifyOAuthTokenApi(token);

      if (payload.accessToken && payload.refreshToken) {
        setToken(payload.accessToken, payload.refreshToken);
        if (payload.email) {
          Cookies.set(ADMIN_EMAIL_KEY, payload.email);
        }
      }

      if (isValidRedirectUrl(redirectUrl)) {
        window.location.replace(redirectUrl);
        return;
      }

      // No handed-off contract on this handoff → plain login.
      if (!contractToken) {
        navigate(AppRoutes.home, { replace: true });
        return;
      }

      const res = await getProductContractHandoffApi({ token: contractToken });
      const { data: contract, isSigned } = res.data;

      const route = resolveHandoffRoute(contract, isSigned);

      // The token is only worth keeping while the create flow still needs to consume it.
      const isCreateRoute = route === AppRoutes.matchCreate;
      if (isCreateRoute) {
        Cookies.set(CONTRACT_TOKEN_KEY, contractToken);
      } else {
        Cookies.remove(CONTRACT_TOKEN_KEY);
      }

      // 初期登録だけが往復になるので、そこにだけ遷移元パスを引き継ぐ。
      navigate(isCreateRoute ? withCenterFrom(route, sourcePath) : route, { replace: true });
    } catch (err) {
      showError(err);
      setTimeout(() => navigate(AppRoutes.login, { replace: true }), 2000);
    }
  }, [token, contractToken, redirectUrl, sourcePath, navigate]);

  useEffect(() => {
    verify();
  }, [verify]);

  return <LoadingScreen />;
};
